import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import classNames from 'classnames';
import { useGameFilters } from 'hooks/gameFilters';
import PropTypes from 'prop-types';

import { useGetGamesListQuery } from 'store/api/configService/gameController';
import { allGamesSelector, selectItem } from 'store/slices/allGames';

import { searchGames } from 'utils/games';

import Card from 'components/GameItem/Card/Card';
import FullInfo from 'components/GameItem/FullInfo/FullInfo';
import Loader from 'components/UI/Loader/Loader';

import { VISIBLE_LIMIT } from 'components/config/games';

import { ReactComponent as EmptyFiltersIcon } from 'images/Icons/EmptyFilters.svg';

import styles from './Content.module.scss';

const SCROLL_BOTTOM_OFFSET = 200;

const Content = ({ viewType, visibleCount, setVisibleCount }) => {
  const dispatch = useDispatch();
  const { provider } = useParams();

  const containerRef = useRef(null);
  const scrollCooldown = useRef(false);

  const search = useSelector(allGamesSelector.getSearch);
  const sortBy = useSelector(allGamesSelector.getSortBy);

  const { filters } = useGameFilters();

  const { data, isFetching } = useGetGamesListQuery({
    sort: sortBy,
    filters,
    provider,
  });

  const games = useMemo(
    () => searchGames(data?.content, search),
    [data, search],
  );

  const visibleGames = useMemo(
    () => games?.slice(0, visibleCount),
    [games, visibleCount],
  );

  const cardView = useMemo(() => viewType === 'column', [viewType]);

  const GameComponent = useMemo(() => (cardView ? Card : FullInfo), [cardView]);

  const handleScroll = useCallback(() => {
    if (scrollCooldown.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    const offsetReached =
      scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_OFFSET;

    if (offsetReached && games?.length > visibleGames?.length) {
      setVisibleCount((prev) => Math.min(prev + VISIBLE_LIMIT, games?.length));
    }
  }, [games, visibleGames?.length]);

  const handleClickCheckbox = useCallback(
    (itemId) => dispatch(selectItem(itemId)),
    [],
  );

  useEffect(() => {
    scrollCooldown.current = true;

    const timeout = setTimeout(() => {
      scrollCooldown.current = false;
    }, 300);

    if (containerRef.current) containerRef.current.scrollTop = 0;

    visibleCount !== VISIBLE_LIMIT && setVisibleCount(VISIBLE_LIMIT);

    return () => clearTimeout(timeout);
  }, [viewType, provider, search, sortBy, filters]);

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  if (isFetching) return <Loader />;

  return (
    <div
      ref={containerRef}
      className={classNames(styles.wrapper, {
        [styles.card]: cardView && visibleGames?.length,
      })}
    >
      {!visibleGames?.length ? (
        <div className={styles.empty}>
          <EmptyFiltersIcon />
          <div>
            <div className={styles.title}>
              We couldn&apos;t find any results for the chosen filters.
            </div>
            <div>Please try changing your filter parameters.</div>
          </div>
        </div>
      ) : (
        visibleGames.map((item) => (
          <GameComponent
            key={item.gameCode}
            item={item}
            onClick={handleClickCheckbox}
            checkbox
          />
        ))
      )}
    </div>
  );
};

Content.propTypes = {
  viewType: PropTypes.oneOf(['row', 'column']).isRequired,
  visibleCount: PropTypes.number.isRequired,
  setVisibleCount: PropTypes.func.isRequired,
};

export default memo(Content);
